import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, EmptyState, Badge } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';
import { ShipModal, PrintResult } from './HazMatShipping';

const PAGE_SIZE = 50;

const STATUS_MAP = {
  processing: 'active', partial: 'warning', fulfilled: 'ok',
  backordered: 'warning', cancelled: 'critical',
};
// Open = processing + partial; Fulfilled = fulfilled + cancelled + everything else
const OPEN_STATUSES      = ['processing', 'partial', 'backordered'];
const FULFILLED_STATUSES = ['fulfilled', 'cancelled'];
const ALL_CHANNELS = ['shopify', 'amazon', 'direct'];

// ── QZ / print helpers (copied from HazMatShipping) ───────────────────────────
async function getQZSignature(request) {
  const resp = await fetch('/api/shipping/qz-sign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request }),
  });
  return (await resp.json()).signature;
}
async function setupQZSecurity() {
  if (!window.qz) return;
  try {
    const cert = await (await fetch('/api/shipping/qz-cert')).text();
    window.qz.security.setCertificatePromise((resolve) => resolve(cert));
    window.qz.security.setSignatureAlgorithm('SHA512');
    window.qz.security.setSignaturePromise((toSign) => (resolve, reject) =>
      getQZSignature(toSign).then(resolve).catch(reject));
  } catch {}
}
async function printViaQZ(printerName, data, isZpl = false) {
  if (!window.qz) throw new Error('QZ Tray not loaded');
  await setupQZSecurity();
  if (!window.qz.websocket.isActive()) await window.qz.websocket.connect();
  const config = isZpl
    ? window.qz.configs.create(printerName)
    : window.qz.configs.create(printerName, { size: { width: 8.5, height: 11 }, units: 'in', scaleContent: true, colorType: 'blackwhite', rasterize: true, density: 300 });
  const printData = isZpl
    ? [{ type: 'raw', format: 'plain', data, options: { language: 'ZPL' } }]
    : [{ type: 'pixel', format: 'base64', data, options: { copies: 1 } }];
  await window.qz.print(config, printData);
}

// ── Utility ───────────────────────────────────────────────────────────────────
function buildParams(filters, page) {
  const p = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
  // Open/Fulfilled toggle → translate to comma-separated status list
  if (filters.openFulfilled === 'open') {
    p.set('status', OPEN_STATUSES.join(','));
  } else if (filters.openFulfilled === 'fulfilled') {
    p.set('status', FULFILLED_STATUSES.join(','));
  } else if (filters.status) {
    p.set('status', filters.status);
  }
  if (filters.channel) p.set('channel', filters.channel);
  if (filters.tags)    p.set('tags', filters.tags);
  if (filters.shippingType === 'hazmat') p.set('hazmat', '1');
  return p.toString();
}


// ── Save View Modal ───────────────────────────────────────────────────────────
function SaveViewModal({ filters, onSave, onClose }) {
  const [name, setName]   = useState('');
  const [shippingType, setShippingType] = useState('none');
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  async function handleSave() {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch('/api/sales-order-views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('sf_token')}` },
        body: JSON.stringify({ name: name.trim(), filters: { ...filters, shippingType: shippingType === 'none' ? null : shippingType } }),
      });
      if (!res.ok) throw new Error(await res.text());
      onSave(await res.json());
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Save View</h3>
          <button className="modal-close" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-danger" style={{ marginBottom:12 }}>{err}</div>}
          <label className="form-label">View Name</label>
          <input ref={inputRef} className="form-control" value={name} onChange={e => { setName(e.target.value); setErr(''); }} onKeyDown={e => e.key === 'Enter' && handleSave()} placeholder="e.g. NMC Orders" style={{ marginBottom:16 }} />

          <label className="form-label">Shipping Type</label>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[['none', 'None', 'Standard view — no shipping actions'],
              ['hazmat', 'HazMat Shipping', 'Adds a Ship button per row for UPS HazMat label creation'],
              ['fedex', 'FedEx Drop Ship', 'Adds checkboxes + Print Labels button for FedEx using account # from order notes'],
            ].map(([val, label, desc]) => (
              <label key={val} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px', border:`2px solid ${shippingType === val ? 'var(--brand)' : 'var(--border)'}`, borderRadius:8, cursor:'pointer', background: shippingType === val ? 'var(--brand-bg, #eff6ff)' : 'transparent' }}>
                <input type="radio" name="shippingType" value={val} checked={shippingType === val} onChange={() => setShippingType(val)} style={{ width:16, height:16, flexShrink:0, marginTop:1 }} />
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{label}</div>
                  <div style={{ fontSize:11, color:'var(--text-secondary)', marginTop:2 }}>{desc}</div>
                </div>
              </label>
            ))}
          </div>

          {(filters.status || filters.channel || filters.tags) && (
            <div style={{ marginTop:14, fontSize:12, color:'var(--text-secondary)' }}>
              <strong>Filters:</strong>
              {filters.status  && <span style={{ marginLeft:6 }}>Status: {filters.status}</span>}
              {filters.channel && <span style={{ marginLeft:6 }}>Channel: {filters.channel}</span>}
              {filters.tags    && <span style={{ marginLeft:6 }}>Tags: {filters.tags}</span>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save View'}</button>
        </div>
      </div>
    </div>
  );
}

// ── ViewTab ───────────────────────────────────────────────────────────────────
function ViewTab({ label, count, active, onClick, onDelete, shippingType }) {
  const icon = shippingType === 'hazmat' ? 'ti-radioactive' : shippingType === 'fedex' ? 'ti-plane' : null;
  return (
    <div onClick={onClick} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', cursor:'pointer', fontSize:13, fontWeight: active ? 600 : 400, color: active ? 'var(--text-primary)' : 'var(--text-secondary)', background: active ? 'var(--surface)' : 'var(--surface-2)', border:'1px solid var(--border)', borderBottom: active ? '1px solid var(--surface)' : '1px solid var(--border)', borderRadius:'6px 6px 0 0', userSelect:'none', position:'relative', bottom: active ? -1 : 0, whiteSpace:'nowrap' }}>
      {icon && <i className={`ti ${icon}`} style={{ fontSize:11 }} />}
      <span>{label}</span>
      {count != null && (
        <span style={{ background: active ? 'var(--brand)' : 'var(--surface-3)', color: active ? '#fff' : 'var(--text-secondary)', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:600, minWidth:20, textAlign:'center' }}>{count}</span>
      )}
      {onDelete && (
        <button onClick={e => { e.stopPropagation(); onDelete(); }} style={{ background:'none', border:'none', cursor:'pointer', padding:'0 0 0 2px', color:'var(--text-tertiary)', fontSize:13, lineHeight:1, display:'flex', alignItems:'center' }} title="Delete view">
          <i className="ti ti-x" />
        </button>
      )}
    </div>
  );
}

// ── Invoice Generator ─────────────────────────────────────────────────────────
function generateInvoiceHtml(order) {
  const items = order.items || [];
  const subtotal = items.reduce((s, i) => s + parseFloat(i.unitPrice || 0) * parseInt(i.quantity || 0), 0);
  const total = parseFloat(order.total_value || subtotal);
  const now = new Date();
  const invoiceNum = `INV-${(order.shopify_order_number || order.order_ref || order.id || '').toString().replace(/[^a-zA-Z0-9-]/g,'')}-${now.getFullYear()}`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${invoiceNum}</title><style>
    *{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:40px}
    h1{font-size:28px;color:#1e40af;margin-bottom:4px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px}
    .company{font-size:12px;color:#555;margin-top:8px;line-height:1.6}
    .invoice-meta{text-align:right;font-size:12px;color:#555;line-height:1.8}
    .invoice-meta strong{color:#111;font-size:14px}
    .section{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:28px}
    .box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px}
    .box h3{font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:8px}
    .box p{line-height:1.7;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    thead tr{background:#1e40af;color:#fff}
    th{text-align:left;padding:9px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.04em}
    th:last-child,td:last-child{text-align:right}
    td{padding:9px 12px;border-bottom:1px solid #e2e8f0;font-size:12px}
    tr:nth-child(even) td{background:#f8fafc}
    .totals{margin-left:auto;width:260px}
    .totals table{margin:0}
    .totals td{border:none;padding:5px 8px}
    .totals tr.grand td{font-weight:700;font-size:15px;border-top:2px solid #1e40af;padding-top:10px;color:#1e40af}
    .footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#888;text-align:center}
    @media print{body{padding:20px}}
  </style></head><body>
  <div class="header">
    <div><h1>INVOICE</h1><div class="company">Summit Fishing Equipment<br>Tdye@summitfishingequipment.com</div></div>
    <div class="invoice-meta">
      <strong>${invoiceNum}</strong><br>
      Date: ${now.toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}<br>
      ${order.order_date ? `Order Date: ${new Date(order.order_date).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}<br>` : ''}
      ${order.channel ? `Channel: ${order.channel.charAt(0).toUpperCase()+order.channel.slice(1)}<br>` : ''}
      Status: <strong>${order.status || 'N/A'}</strong>
    </div>
  </div>
  <div class="section">
    <div class="box"><h3>Bill To / Ship To</h3><p>
      <strong>${order.customer_name || order.shipping_name || '&#8212;'}</strong><br>
      ${order.shipping_address1 ? order.shipping_address1+'<br>' : ''}
      ${order.shipping_address2 ? order.shipping_address2+'<br>' : ''}
      ${order.shipping_city ? order.shipping_city+', '+order.shipping_state+' '+order.shipping_zip : ''}
    </p></div>
    <div class="box"><h3>Order Details</h3><p>
      Order #: <strong>${order.shopify_order_number || order.order_ref || '&#8212;'}</strong><br>
      ${order.tags ? 'Tags: '+order.tags+'<br>' : ''}
      ${order.notes ? 'Notes: '+order.notes : ''}
    </p></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>SKU</th><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${items.map((item, idx) => `<tr>
      <td>${idx+1}</td>
      <td style="font-family:monospace;font-size:11px">${item.sku||'&#8212;'}</td>
      <td>${item.productName||'&#8212;'}</td>
      <td style="text-align:right">${item.quantity||0}</td>
      <td style="text-align:right">$${parseFloat(item.unitPrice||0).toFixed(2)}</td>
      <td style="text-align:right">$${(parseFloat(item.unitPrice||0)*parseInt(item.quantity||0)).toFixed(2)}</td>
    </tr>`).join('')}</tbody>
  </table>
  <div class="totals"><table>
    <tr><td>Subtotal</td><td>$${subtotal.toFixed(2)}</td></tr>
    ${total !== subtotal ? `<tr><td>Adjustments</td><td>$${(total-subtotal).toFixed(2)}</td></tr>` : ''}
    <tr class="grand"><td>TOTAL</td><td>$${total.toFixed(2)}</td></tr>
  </table></div>
  <div class="footer">Thank you for your business! &nbsp;&middot;&nbsp; Generated by StockForge &nbsp;&middot;&nbsp; ${now.toISOString()}</div>
  </body></html>`;
}

function handlePrintInvoice(order) {
  const html = generateInvoiceHtml(order);
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { alert('Please allow popups to print invoices.'); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch {} }, 800);
}

// ── FedEx Box Modal ───────────────────────────────────────────────────────────
// Opens when user clicks "Add Box". Shows all order items with qty controls,
// box size selector or custom dims, and auto-calculated weight with manual override.
function FedexBoxModal({ order, boxSizes, stagedBoxes, onAdd, onClose }) {
  const items = order.items || [];

  // Track qty already staged in boxes awaiting print
  const alreadyBoxedQty = {};
  stagedBoxes.forEach(box => box.items.forEach(bi => {
    alreadyBoxedQty[bi.salesOrderItemId] = (alreadyBoxedQty[bi.salesOrderItemId] || 0) + bi.qty;
  }));

  // Per-item qty state: default to remaining (fulfillable - already-boxed)
  const initialQtys = {};
  items.forEach(item => {
    const maxQty = item.fulfillableQuantity != null ? item.fulfillableQuantity : Math.max(0, (item.quantity || 0) - (item.fulfilledQuantity || 0));
    const boxed  = alreadyBoxedQty[item.id] || 0;
    const remaining = Math.max(0, maxQty - boxed);
    initialQtys[item.id] = remaining;
  });

  const defaultBox   = boxSizes?.[0] || null;
  const [itemQtys,   setItemQtys]   = useState(initialQtys);
  const [boxSizeId,  setBoxSizeId]  = useState(defaultBox?.id || '');
  const [customDims, setCustomDims] = useState(false);       // true = use custom length/width/height
  const [dimL,       setDimL]       = useState('');
  const [dimW,       setDimW]       = useState('');
  const [dimH,       setDimH]       = useState('');
  const [tare,       setTare]       = useState(defaultBox ? parseFloat(defaultBox.weight_lbs || 0) : 0);
  const [weightOverride, setWeightOverride] = useState(false);
  const [manualWeight,   setManualWeight]   = useState('');

  // Update tare when box size changes
  function handleBoxSizeChange(id) {
    setBoxSizeId(id);
    const bs = boxSizes?.find(b => b.id === parseInt(id));
    const newTare = bs ? parseFloat(bs.weight_lbs || 0) : 0;
    setTare(newTare);
    if (bs) setCustomDims(false);
  }

  // Auto-calculated weight: sum(item.weight * qty) + tare
  const autoWeight = items.reduce((sum, item) => {
    const productWeight = parseFloat(item.weight || 0);
    const qty = itemQtys[item.id] || 0;
    return sum + productWeight * qty;
  }, tare);

  const displayWeight = weightOverride ? parseFloat(manualWeight) || 0 : autoWeight;

  function handleAdd() {
    const selectedItems = items.filter(item => {
      const maxQty = item.fulfillableQuantity != null ? item.fulfillableQuantity : Math.max(0, (item.quantity || 0) - (item.fulfilledQuantity || 0));
      const boxed  = alreadyBoxedQty[item.id] || 0;
      return (itemQtys[item.id] || 0) > 0 && maxQty - boxed > 0;
    });
    if (!selectedItems.length) { alert('Enter a quantity for at least one item.'); return; }

    const selectedBoxSize = customDims ? null : (boxSizes?.find(b => b.id === parseInt(boxSizeId)) || null);
    const dimensions = customDims
      ? (dimL && dimW && dimH ? { length: parseFloat(dimL), width: parseFloat(dimW), height: parseFloat(dimH) } : null)
      : (selectedBoxSize ? { length: parseFloat(selectedBoxSize.length_in), width: parseFloat(selectedBoxSize.width_in), height: parseFloat(selectedBoxSize.height_in) } : null);

    onAdd({
      id: Date.now(),
      items: selectedItems.map(item => ({
        salesOrderItemId: item.id,
        itemId:  item.id,
        qty:     itemQtys[item.id] || 0,
        sku:     item.sku,
        name:    item.productName || item.name || '',
        weight:  parseFloat(item.weight || 0),
      })),
      boxSizeId:  selectedBoxSize?.id || null,
      dimensions,
      weightLbs:  parseFloat(displayWeight.toFixed(2)) || 0.5,
    });
    onClose();
  }

  const overlayStyle = {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
    display:'flex', alignItems:'center', justifyContent:'center',
  };
  const modalStyle = {
    background:'var(--surface-1, #fff)', borderRadius:8, boxShadow:'0 8px 32px rgba(0,0,0,0.22)',
    width:600, maxWidth:'95vw', maxHeight:'88vh', overflow:'auto', padding:0,
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontWeight:700, fontSize:15 }}>Configure Box</span>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'var(--text-secondary)', lineHeight:1 }}>×</button>
        </div>

        {/* Item qty table */}
        <div style={{ padding:'14px 18px' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:8 }}>Items</div>
          <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)' }}>
                <th style={{ textAlign:'left', padding:'4px 6px' }}>SKU</th>
                <th style={{ textAlign:'left', padding:'4px 6px' }}>Product</th>
                <th style={{ textAlign:'right', padding:'4px 6px' }}>Ordered</th>
                <th style={{ textAlign:'right', padding:'4px 6px' }}>Boxed</th>
                <th style={{ textAlign:'right', padding:'4px 6px' }}>Remaining</th>
                <th style={{ textAlign:'center', padding:'4px 6px', width:70 }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const maxQty    = item.fulfillableQuantity != null ? item.fulfillableQuantity : Math.max(0, (item.quantity || 0) - (item.fulfilledQuantity || 0));
                const boxed     = alreadyBoxedQty[item.id] || 0;
                const remaining = Math.max(0, maxQty - boxed);
                const qty       = itemQtys[item.id] !== undefined ? itemQtys[item.id] : remaining;
                return (
                  <tr key={i} style={{ borderBottom:'1px solid var(--border-subtle, #f0f0f0)', opacity: remaining === 0 ? 0.45 : 1 }}>
                    <td style={{ fontFamily:'monospace', padding:'5px 6px', fontSize:11 }}>{item.sku}</td>
                    <td style={{ padding:'5px 6px' }}>{item.productName || item.name}</td>
                    <td style={{ textAlign:'right', padding:'5px 6px', color:'var(--text-secondary)' }}>{item.quantity}</td>
                    <td style={{ textAlign:'right', padding:'5px 6px', color:'var(--text-tertiary)' }}>{(item.fulfilledQuantity || 0) + boxed}</td>
                    <td style={{ textAlign:'right', padding:'5px 6px', fontWeight: remaining > 0 ? 600 : 'normal' }}>{remaining}</td>
                    <td style={{ textAlign:'center', padding:'3px 6px' }}>
                      <input type="number" min={0} max={remaining} value={qty}
                        disabled={remaining === 0}
                        onChange={e => setItemQtys(prev => ({ ...prev, [item.id]: Math.min(remaining, Math.max(0, parseInt(e.target.value) || 0)) }))}
                        style={{ width:52, textAlign:'center', padding:'3px 4px', border:'1px solid var(--border)', borderRadius:3, fontSize:11 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Box size / dims */}
        <div style={{ padding:'0 18px 14px', borderTop:'1px solid var(--border)' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em', margin:'12px 0 8px' }}>Box Size</div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {!customDims && (
              <select value={boxSizeId} onChange={e => handleBoxSizeChange(e.target.value)}
                style={{ fontSize:12, padding:'5px 8px', borderRadius:4, border:'1px solid var(--border)', minWidth:180 }}>
                <option value="">— Select predefined size —</option>
                {(boxSizes || []).map(bs => (
                  <option key={bs.id} value={bs.id}>{bs.name} ({bs.length_in}×{bs.width_in}×{bs.height_in}")</option>
                ))}
              </select>
            )}
            {customDims && (
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="number" min="1" placeholder="L" value={dimL} onChange={e => setDimL(e.target.value)}
                  style={{ width:52, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:3, fontSize:12 }} />
                <span style={{ color:'var(--text-secondary)' }}>×</span>
                <input type="number" min="1" placeholder="W" value={dimW} onChange={e => setDimW(e.target.value)}
                  style={{ width:52, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:3, fontSize:12 }} />
                <span style={{ color:'var(--text-secondary)' }}>×</span>
                <input type="number" min="1" placeholder="H" value={dimH} onChange={e => setDimH(e.target.value)}
                  style={{ width:52, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:3, fontSize:12 }} />
                <span style={{ fontSize:11, color:'var(--text-secondary)' }}>in</span>
                <input type="number" min="0" step="0.1" placeholder="Tare lbs" value={tare}
                  onChange={e => setTare(parseFloat(e.target.value) || 0)}
                  style={{ width:72, padding:'4px 6px', border:'1px solid var(--border)', borderRadius:3, fontSize:12 }} />
                <span style={{ fontSize:11, color:'var(--text-secondary)' }}>lbs tare</span>
              </div>
            )}
            <button type="button" onClick={() => setCustomDims(d => !d)}
              style={{ fontSize:11, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:4, background:'none', cursor:'pointer', color:'var(--text-secondary)' }}>
              {customDims ? '← Predefined' : 'Custom dims'}
            </button>
          </div>
        </div>

        {/* Weight */}
        <div style={{ padding:'0 18px 14px' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:8 }}>Total Weight</div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {!weightOverride && (
              <span style={{ fontSize:14, fontWeight:600 }}>{autoWeight.toFixed(2)} lbs</span>
            )}
            {!weightOverride && (
              <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                (items {(autoWeight - tare).toFixed(2)} + box {tare.toFixed(2)})
              </span>
            )}
            {weightOverride && (
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="number" min="0.1" step="0.1" value={manualWeight}
                  onChange={e => setManualWeight(e.target.value)}
                  style={{ width:80, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:3, fontSize:13 }} />
                <span style={{ fontSize:12, color:'var(--text-secondary)' }}>lbs</span>
              </div>
            )}
            <button type="button" onClick={() => { setWeightOverride(o => !o); if (!weightOverride) setManualWeight(autoWeight.toFixed(2)); }}
              style={{ fontSize:11, padding:'3px 8px', border:'1px solid var(--border)', borderRadius:4, background:'none', cursor:'pointer', color:'var(--text-secondary)' }}>
              {weightOverride ? 'Use auto' : 'Override'}
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ padding:'12px 18px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'7px 16px', border:'1px solid var(--border)', borderRadius:5, background:'none', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={handleAdd} className="btn btn-primary"
            style={{ padding:'7px 16px', border:'none', borderRadius:5, background:'#4f46e5', color:'white', cursor:'pointer', fontSize:13, fontWeight:600 }}>
            Stage Box
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Print Orders Modal ────────────────────────────────────────────────────────
// Shows each selected order with its staged boxes. Auto-groups items into 1 box
// if none staged. Split Shipment adds a 2nd box. Qty inputs per item per box.
function PrintOrdersModal({ orders, fedexBoxState, boxSizes, onClose, onPrint, initialSelections }) {
  // Helper: pick default box size id based on order items
  function getDefaultBoxSizeId(order) {
    if (!boxSizes || !boxSizes.length) return null;
    const items = order.items || [];
    // Check for 20AH batteries (sku or name contains '20AH' or '20 AH')
    const has20ah = items.some(item =>
      /20[\s-]?ah/i.test(item.sku || '') || /20[\s-]?ah/i.test(item.productName || item.name || '')
    );
    // Check for 32AH or 52AH batteries
    const has3252ah = items.some(item =>
      /(?:32|52)[\s-]?ah/i.test(item.sku || '') || /(?:32|52)[\s-]?ah/i.test(item.productName || item.name || '')
    );
    if (has20ah) {
      const bs = boxSizes.find(b => /20[\s-]?ah/i.test(b.name));
      if (bs) return bs.id;
    }
    if (has3252ah) {
      // Match "Battery Box" but NOT "20AH Battery Box"
      const bs = boxSizes.find(b => /battery\s+box/i.test(b.name) && !/20[\s-]?ah/i.test(b.name));
      if (bs) return bs.id;
    }
    return null;
  }

  // Build per-order box state (local copy so edits don't affect staged state until Print)
  const [localBoxes, setLocalBoxes] = useState(() => {
    const init = {};
    orders.forEach(o => {
      const staged = fedexBoxState[o.id]?.boxes || [];
      if (staged.length > 0) {
        // Deep copy staged boxes
        init[o.id] = staged.map(b => ({ ...b, items: b.items.map(i => ({ ...i })) }));
      } else {
        // Build full item list (ALL items, qty may be 0 for unchecked)
        const allItems = (o.items || []).map(item => {
          const remaining = item.fulfillableQuantity != null
            ? item.fulfillableQuantity
            : Math.max(0, (item.quantity || 0) - (item.fulfilledQuantity || 0));
          return {
            salesOrderItemId: item.id,
            itemId: item.id,
            sku: item.sku,
            name: item.productName || item.name || '',
            qty: remaining > 0 ? remaining : 0,
            orderedQty: item.quantity || 0,
            remainingQty: Math.max(0, remaining),
            remainingQty: Math.max(0, remaining),
          };
        });
        if (initialSelections && initialSelections[o.id]) {
          // Use row-level item selections: checked items get their shipQty, unchecked get 0
          const sel = initialSelections[o.id];
          const items = allItems.map(bi => {
            const item = (o.items || []).find(it => it.id === (bi.salesOrderItemId || bi.itemId));
            const itemSel = item ? (sel[item.id] || {}) : {};
            const wh = parseFloat(item?.hazmatWhPerUnit || item?.hazmat_wh_per_unit || 0);
            const hasFR = (o.items || []).some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
            const defaultChecked = hasFR ? wh > 256 : false;
            const availableQty = bi.qty; // already 0 for fulfilled items
            const checked = itemSel.checked !== undefined ? itemSel.checked : (availableQty > 0 && defaultChecked);
            const shipQty = itemSel.qty !== undefined ? Math.min(itemSel.qty, availableQty) : availableQty;
            return { ...bi, qty: (checked && availableQty > 0) ? Math.max(0, shipQty) : 0 };
          });
          init[o.id] = [{ id: Date.now() + o.id, boxSizeId: getDefaultBoxSizeId(o), items }];
        } else {
          // Auto-group: include all items (non-zero qty first, zeros at end)
          const items = allItems.filter(i => i.qty > 0).concat(allItems.filter(i => i.qty <= 0));
          init[o.id] = [{ id: Date.now() + o.id, boxSizeId: getDefaultBoxSizeId(o), items }];
        }
      }
    });
    return init;
  });

  function getOrderedQty(order, sku) {
    const item = (order.items || []).find(i => i.sku === sku);
    return item ? (item.quantity || 0) : 0;
  }

  function getTotalAssigned(orderId, salesOrderItemId) {
    const boxes = localBoxes[orderId] || [];
    return boxes.reduce((sum, box) => {
      const bi = box.items.find(i => i.salesOrderItemId === salesOrderItemId || i.itemId === salesOrderItemId);
      return sum + (bi ? bi.qty : 0);
    }, 0);
  }

  function setItemQty(orderId, boxId, salesOrderItemId, newQty) {
    setLocalBoxes(prev => ({
      ...prev,
      [orderId]: (prev[orderId] || []).map(box =>
        box.id !== boxId ? box : {
          ...box,
          items: box.items.map(bi =>
            (bi.salesOrderItemId === salesOrderItemId || bi.itemId === salesOrderItemId)
              ? { ...bi, qty: Math.max(0, newQty) }
              : bi
          ),
        }
      ),
    }));
  }

  function splitShipment(orderId, order) {
    const firstBox = (localBoxes[orderId] || [])[0];
    if (!firstBox) return;
    // New box includes ALL order items (derived from order.items so none are missing), qty 0
    const allItems = (order.items || []).map(item => {
      const existing = firstBox.items.find(bi => (bi.salesOrderItemId || bi.itemId) === item.id);
      return existing
        ? { ...existing, qty: 0 }
        : {
            salesOrderItemId: item.id,
            itemId: item.id,
            sku: item.sku,
            name: item.productName || item.name || '',
            qty: 0,
            orderedQty: item.quantity || 0,
          };
    });
    const newBox = {
      id: Date.now(),
      boxSizeId: getDefaultBoxSizeId(order),
      items: allItems,
    };
    setLocalBoxes(prev => ({
      ...prev,
      [orderId]: [...(prev[orderId] || []), newBox],
    }));
  }

  function removeBox(orderId, boxId) {
    setLocalBoxes(prev => ({
      ...prev,
      [orderId]: (prev[orderId] || []).filter(b => b.id !== boxId),
    }));
  }

  function setBoxSize(orderId, boxId, boxSizeId) {
    setLocalBoxes(prev => ({
      ...prev,
      [orderId]: (prev[orderId] || []).map(box =>
        box.id !== boxId ? box : { ...box, boxSizeId: boxSizeId || null }
      ),
    }));
  }

  // Validation: for each order, for each item, sum across boxes must not exceed orderedQty
  function getValidationErrors() {
    const errors = [];
    orders.forEach(order => {
      const boxes = localBoxes[order.id] || [];
      (order.items || []).forEach(item => {
        const total = boxes.reduce((s, b) => {
          const bi = b.items.find(i => i.salesOrderItemId === item.id || i.itemId === item.id);
          return s + (bi ? bi.qty : 0);
        }, 0);
        if (total > (item.quantity || 0)) {
          errors.push(`${order.shopify_order_number || order.order_ref}: ${item.sku} over-allocated (${total} > ${item.quantity})`);
        }
      });
    });
    return errors;
  }

  const validationErrors = getValidationErrors();
  const canPrint = validationErrors.length === 0;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '32px 16px', overflowY: 'auto',
  };
  const modalStyle = {
    background: '#ffffff', borderRadius: 10,
    boxShadow: '0 20px 48px rgba(0,0,0,0.22)', width: '100%', maxWidth: 780,
    display: 'flex', flexDirection: 'column', border: '1px solid var(--border)',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Print Orders</span>
            <span style={{ background: 'var(--brand, #2563eb)', color: '#fff', borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>{orders.length} order{orders.length !== 1 ? 's' : ''}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}>×</button>
        </div>

        {/* Order cards */}
        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          {orders.map(order => {
            const boxes = localBoxes[order.id] || [];
            const orderRef = order.shopify_order_number || order.order_ref?.replace(/^shopify-\d+-/, '#') || `#${order.id}`;

            return (
              <div key={order.id} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--brand, #2563eb)', fontFamily: 'monospace' }}>{orderRef}</span>
                  {order.customer_name && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{order.customer_name}</span>}
                  <span style={{ fontSize: 11, background: boxes.length > 1 ? '#fef3c7' : 'var(--brand-bg, #eff6ff)', color: boxes.length > 1 ? '#92400e' : 'var(--brand, #2563eb)', borderRadius: 99, padding: '1px 8px', fontWeight: 600 }}>
                    {boxes.length === 1 ? 'Auto-grouped' : `${boxes.length} boxes`}
                  </span>
                  <div style={{ marginLeft: 'auto' }}>
                    <button
                      onClick={() => splitShipment(order.id, order)}
                      style={{ fontSize: 12, padding: '5px 12px', border: '2px solid var(--brand, #2563eb)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--brand, #2563eb)', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-bg, #eff6ff)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 15a9 9 0 0 0 9 9"/><path d="M6 3a9 9 0 0 1 9 9"/></svg>
                      Split Shipment
                    </button>
                  </div>
                </div>

                {/* Boxes */}
                {boxes.map((box, bi) => (
                  <div key={box.id} style={{ borderTop: bi > 0 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 12 }}>Box {bi + 1}</span>
                      {boxSizes && boxSizes.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--brand-bg, #eff6ff)', border: '1.5px solid var(--brand, #2563eb)', borderRadius: 6, padding: '3px 8px 3px 6px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand, #2563eb)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                          <select
                            value={box.boxSizeId != null ? String(box.boxSizeId) : ''}
                            onChange={e => setBoxSize(order.id, box.id, e.target.value || null)}
                            style={{ fontSize: 12, padding: '1px 4px', border: 'none', borderRadius: 3, background: 'transparent', color: 'var(--brand, #2563eb)', cursor: 'pointer', fontWeight: 600, outline: 'none' }}>
                            <option value="">0″ × 0″ × 0″</option>
                            {boxSizes.map(bs => (
                              <option key={bs.id} value={String(bs.id)}>
                                {bs.name}{bs.length_in && bs.width_in && bs.height_in ? ` (${bs.length_in}×${bs.width_in}×${bs.height_in}")` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {boxes.length > 1 && (
                        <button onClick={() => removeBox(order.id, box.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 13, padding: '0 4px', marginLeft: 'auto', borderRadius: 3 }}
                          title="Remove box">✕</button>
                      )}
                    </div>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 4 }}>
                      <tbody>
                        {box.items.map((bi2, ii) => {
                          const orderedQty = getOrderedQty(order, bi2.sku) || bi2.orderedQty || 0;
                          const totalAssigned = boxes.reduce((s, b) => {
                            const found = b.items.find(i => (i.salesOrderItemId || i.itemId) === (bi2.salesOrderItemId || bi2.itemId));
                            return s + (found ? found.qty : 0);
                          }, 0);
                          const isOver = totalAssigned > orderedQty;
                          return (
                            <tr key={ii} style={{ borderBottom: '1px solid var(--border-subtle, #f0f0f0)' }}>
                              <td style={{ padding: '5px 14px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)', width: 110 }}>{bi2.sku}</td>
                              <td style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>{bi2.name}</td>
                              <td style={{ padding: '5px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <input
                                    type="number" min={0} max={orderedQty}
                                    value={bi2.qty}
                                    onChange={e => {
                                      let v = parseInt(e.target.value) || 0;
                                      if (v < 0) v = 0;
                                      const maxQty = bi2.remainingQty !== undefined ? bi2.remainingQty : orderedQty;
                                      if (v > maxQty) v = maxQty;
                                      setItemQty(order.id, box.id, bi2.salesOrderItemId || bi2.itemId, v);
                                    }}
                                    style={{ width: 52, textAlign: 'center', padding: '3px 5px', border: `1px solid ${isOver ? '#dc2626' : 'var(--border)'}`, borderRadius: 4, fontSize: 12, fontWeight: 600, background: 'var(--surface)', color: isOver ? '#dc2626' : 'var(--text)' }}
                                  />
                                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>/ {bi2.remainingQty !== undefined ? bi2.remainingQty : orderedQty}</span>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}

                {/* Per-order validation */}
                {(() => {
                  const orderErrors = validationErrors.filter(e => e.startsWith(orderRef));
                  if (!orderErrors.length) return null;
                  return (
                    <div style={{ padding: '6px 14px 8px', background: '#fef2f2', borderTop: '1px solid #fecaca', fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
                      ✕ {orderErrors.map(e => e.split(': ').slice(1).join(': ')).join('; ')} — fix before printing
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text)' }}>{orders.length} orders</strong> · <strong style={{ color: 'var(--text)' }}>{Object.values(localBoxes).reduce((s, b) => s + b.length, 0)} boxes</strong> total
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '7px 16px', border: '1px solid var(--border)', borderRadius: 5, background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>Cancel</button>
            <button
              onClick={() => canPrint && onPrint(localBoxes)}
              disabled={!canPrint}
              title={!canPrint ? validationErrors[0] : ''}
              style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: canPrint ? '#4f46e5' : 'var(--border)', color: canPrint ? 'white' : 'var(--text-tertiary)', cursor: canPrint ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: canPrint ? 1 : 0.6 }}>
              <i className="ti ti-printer" /> Print Labels
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

async function pickPrinter(printers) {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:99999;display:flex;align-items:center;justify-content:center';
    const box = document.createElement('div');
    box.style.cssText = 'background:white;border-radius:10px;padding:24px;width:340px;box-shadow:0 20px 60px rgba(0,0,0,0.4)';
    box.innerHTML = '<div style="font-weight:600;font-size:15px;margin-bottom:16px">Select Printer</div>';
    printers.forEach(p => {
      const btn = document.createElement('button');
      btn.textContent = p;
      btn.style.cssText = 'display:block;width:100%;text-align:left;padding:10px 14px;margin-bottom:8px;border:1px solid #ddd;border-radius:6px;cursor:pointer;font-size:13px;background:white';
      btn.onmouseover = () => btn.style.background = '#f0f7ff';
      btn.onmouseout  = () => btn.style.background = 'white';
      btn.onclick = () => { document.body.removeChild(overlay); resolve(p); };
      box.appendChild(btn);
    });
    const cancel = document.createElement('button');
    cancel.textContent = 'Cancel';
    cancel.style.cssText = 'display:block;width:100%;padding:8px;border:none;cursor:pointer;font-size:12px;color:#666;margin-top:4px;background:transparent';
    cancel.onclick = () => { document.body.removeChild(overlay); resolve(null); };
    box.appendChild(cancel);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
  });
}

export default function SalesOrders() {
  const [page, setPage]         = useState(0);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters]   = useState({ status: '', openFulfilled: 'open', channel: '', tags: '', shippingType: null });
  const [views, setViews]       = useState([]);
  const [viewsLoading, setViewsLoading] = useState(true);
  const [activeViewId, setActiveViewId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [viewCounts, setViewCounts] = useState({});
  const [allCount, setAllCount]     = useState(null);

  // HazMat ship state
  const [shipModal, setShipModal]   = useState(null); // { order, selections }
  const [printResult, setPrintResult] = useState(null);
  const [itemSelections, setItemSelections] = useState({});
  const [shipmentMap, setShipmentMap] = useState({});
  const { data: shippingSettings } = useFetch('/api/shipping/settings');
  const { data: rawBoxSizes } = useFetch('/api/box-sizes');
  const boxSizes = (rawBoxSizes || []).filter(b => b.active);

  // FedEx state
  const [fedexSelected, setFedexSelected] = useState(new Set());
  const [hazmatSelected, setHazmatSelected] = useState(new Set());
  const [hazmatPrinting, setHazmatPrinting] = useState(null);
  const [hazmatPrintModalOpen, setHazmatPrintModalOpen] = useState(false);
  const [fedexService, setFedexService] = useState('FEDEX_GROUND'); // single service for all selected
  const [fedexPrinting, setFedexPrinting] = useState(null); // null | { current, total, orderRef, done }
  // FedEx multi-box state: { [orderId]: { boxes: [{id, items:[{salesOrderItemId,itemId,qty,sku,name,weight}], boxSizeId, dimensions, weightLbs}] } }
  const [fedexBoxState, setFedexBoxState] = useState({});
  const [fedexBoxModal, setFedexBoxModal] = useState(null); // null | { order }

  // Print Orders modal (carrier-agnostic)
  const [printModalOpen, setPrintModalOpen] = useState(false);

  // HazMat manifest state
  const [generatingManifest, setGeneratingManifest] = useState(false);
  const [unmanifested, setUnmanifested] = useState({ count: 0, shipments: [] });
  const [checkedManifestIds, setCheckedManifestIds] = useState(new Set());

  const activeView = views.find(v => v.id === activeViewId) || null;
  const shippingType = activeView?.filters?.shippingType || null;

  // Load views
  useEffect(() => {
    fetch('/api/sales-order-views', { headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` } })
      .then(r => r.json()).then(d => { setViews(Array.isArray(d) ? d : []); setViewsLoading(false); })
      .catch(() => setViewsLoading(false));
  }, []);

  // Fetch tab counts
  useEffect(() => {
    fetch('/api/sales-orders?limit=1&offset=0', { headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` } })
      .then(r => r.json()).then(d => setAllCount(d.total || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    views.forEach(v => {
      const p = buildParams({ status: '', channel: '', tags: '', ...v.filters }, 0);
      fetch(`/api/sales-orders?${p}&limit=1&offset=0`, { headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` } })
        .then(r => r.json()).then(d => setViewCounts(prev => ({ ...prev, [v.id]: d.total || 0 }))).catch(() => {});
    });
  }, [views]);

  function activateView(view) {
    setActiveViewId(view ? view.id : null);
    setFilters(view ? { status: '', openFulfilled: 'open', channel: '', tags: '', shippingType: null, ...view.filters } : { status: '', openFulfilled: 'open', channel: '', tags: '', shippingType: null });
    setPage(0); setSearch(''); setFedexSelected(new Set());
  }

  const params = buildParams(filters, page);
  const { data, loading, error, refetch } = useFetch(`/api/sales-orders?${params}`, [params]);

  // Auto-load shipments for HazMat and FedEx views
  const allOrders = data?.orders || [];
  useEffect(() => {
    if (shippingType !== 'hazmat' && shippingType !== 'fedex') return;
    allOrders.forEach(o => { if (!shipmentMap[o.id]) loadShipments(o.id); });
  }, [allOrders, shippingType]); // eslint-disable-line

  const loadShipments = useCallback(async (orderId) => {
    try {
      const { data: shipments } = await api.get(`/api/shipping/order/${orderId}`);
      setShipmentMap(m => ({ ...m, [orderId]: shipments }));
    } catch {}
  }, []);

  const orders = allOrders.filter(o =>
    !search ||
    (o.shopify_order_number || o.order_ref)?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.tags?.toLowerCase().includes(search.toLowerCase())
  );
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  async function deleteView(view) {
    if (!window.confirm(`Delete view "${view.name}"?`)) return;
    try {
      const res = await fetch(`/api/sales-order-views/${view.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` } });
      if (!res.ok) throw new Error(await res.text());
      setViews(prev => prev.filter(v => v.id !== view.id));
      if (activeViewId === view.id) activateView(null);
    } catch (e) { alert('Failed to delete view: ' + e.message); }
  }

  function handleSaveView(newView) {
    setViews(prev => [...prev, newView].sort((a, b) => a.name.localeCompare(b.name)));
    setShowSaveModal(false);
    const p = buildParams({ status: '', channel: '', tags: '', ...newView.filters }, 0);
    fetch(`/api/sales-orders?${p}&limit=1&offset=0`, { headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` } })
      .then(r => r.json()).then(d => setViewCounts(prev => ({ ...prev, [newView.id]: d.total || 0 }))).catch(() => {});
  }

  // HazMat: reprint label
  const reprintLabel = async (shipment) => {
    setHazmatPrinting({ current: 1, total: 1, orderRef: shipment.tracking_number || 'label', done: false });
    try {
      const { data } = await api.get(`/api/shipping/${shipment.id}/reprint`);
      const lp = shippingSettings?.zebra_printer_name || 'ZDesigner ZT231';
      try {
        await printViaQZ(lp, data.labelZpl, true);
        if (data.packingSlipZpl) await printViaQZ(lp, data.packingSlipZpl, true);
        setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 1, failed: 0 });
      } catch {
        const blob = new Blob([data.labelZpl], { type: 'text/plain' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
        a.download = `label_${shipment.tracking_number}.zpl`; a.click();
        setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 0, failed: 1, errorMsg: 'QZ not available — downloaded ZPL file' });
      }
    } catch (err) {
      setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 0, failed: 1, errorMsg: err?.response?.data?.error || err.message });
    }
    setTimeout(() => setHazmatPrinting(null), 5000);
  };

  // HazMat: reprint form
  const reprintForm = async (shipment) => {
    setHazmatPrinting({ current: 1, total: 1, orderRef: shipment.tracking_number || 'form', done: false });
    try {
      const { data } = await api.post('/api/shipping/hazmat-form', { shipmentId: shipment.id });
      if (!data.hazmatFormPdf) {
        setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 0, failed: 1, errorMsg: 'No HazMat form found for this shipment' });
        setTimeout(() => setHazmatPrinting(null), 5000);
        return;
      }
      const printerName = shippingSettings?.hazmat_printer_name || 'HAZMAT Printer';
      try {
        await printViaQZ(printerName, data.hazmatFormPdf, false);
        setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 1, failed: 0 });
      } catch {
        setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 0, failed: 1, errorMsg: 'Form reprint failed — QZ not available' });
      }
    } catch (err) {
      setHazmatPrinting({ current: 1, total: 1, orderRef: '', done: true, printed: 0, failed: 1, errorMsg: err?.response?.data?.error || err.message });
    }
    setTimeout(() => setHazmatPrinting(null), 5000);
  };

  // HazMat manifest
  const fetchUnmanifested = useCallback(async () => {
    try {
      const { data } = await api.get('/api/shipping/unmanifested');
      setUnmanifested(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnmanifested();
    const interval = setInterval(fetchUnmanifested, 30000);
    return () => clearInterval(interval);
  }, [fetchUnmanifested]);

  const generateManifest = async (shipmentIds) => {
    if (!shipmentIds?.length) return;
    setGeneratingManifest(true);
    try {
      const { data: result } = await api.post('/api/shipping/manifest', { shipmentIds });
      const manifestPrinterName = shippingSettings?.manifest_printer_name || 'Brother MFC-L8900CDW series Printer';
      let chosen = manifestPrinterName;
      try {
        const allPrinters = await window.qz.printers.find();
        // Find exact match first, then partial match
        chosen = allPrinters.find(p => p === manifestPrinterName)
          || allPrinters.find(p => p.toLowerCase().includes(manifestPrinterName.toLowerCase()))
          || manifestPrinterName;
      } catch {}
      if (chosen) {
        try {
          await printViaQZ(chosen, result.manifestPdf, false);
        } catch {
          const byteArr = Uint8Array.from(atob(result.manifestPdf), c => c.charCodeAt(0));
          const blob = new Blob([byteArr], { type: 'application/pdf' });
          window.open(URL.createObjectURL(blob), '_blank');
        }
      }
      setCheckedManifestIds(prev => {
        const next = new Set(prev);
        shipmentIds.forEach(id => next.delete(id));
        return next;
      });
      fetchUnmanifested();
      refetch();
    } catch (err) { alert(err?.response?.data?.error || err.message); }
    finally { setGeneratingManifest(false); }
  };

  const printManifest = async () => {
    let ids;
    if (checkedManifestIds.size > 0) {
      ids = [...checkedManifestIds];
    } else if (hazmatSelected.size > 0 && filters.openFulfilled === 'fulfilled') {
      const orderIds = [...hazmatSelected];
      const resolvedMap = Object.assign({}, shipmentMap);
      await Promise.all(orderIds.filter(function(id){ return !resolvedMap[id]; }).map(async function(id){
        try {
          const resp = await api.get('/api/shipping/order/' + id);
          resolvedMap[id] = resp.data;
        } catch(e) {}
      }));
      ids = orderIds.flatMap(function(orderId){
        const ships = Array.isArray(resolvedMap[orderId]) ? resolvedMap[orderId] : (resolvedMap[orderId] ? [resolvedMap[orderId]] : []);
        return ships.filter(function(s){ return s && s.id && s.ship_type === 'hazmat' && s.status !== 'voided'; }).map(function(s){ return s.id; });
      });
    } else if (unmanifested.shipments.length > 0) {
      ids = unmanifested.shipments.map(s => s.id);
    } else if (expanded && shipmentMap[expanded]) {
      const expandedShipments = Array.isArray(shipmentMap[expanded])
        ? shipmentMap[expanded]
        : [shipmentMap[expanded]];
      ids = expandedShipments.filter(s => s && s.id && s.ship_type === 'hazmat' && s.status !== 'voided').map(s => s.id);
    }
    if (!ids || !ids.length) return;
    const count = ids.length;
    if (!window.confirm('Print manifest for ' + count + ' shipment' + (count > 1 ? 's' : '') + '?')) return;
    await generateManifest(ids);
  };

  // FedEx multi-box helpers
  function getFedexOrderState(orderId) {
    return fedexBoxState[orderId] || { boxes: [] };
  }
  function setFedexOrderState(orderId, updater) {
    setFedexBoxState(prev => ({ ...prev, [orderId]: updater(prev[orderId] || { boxes: [] }) }));
  }

  function openFedexBoxModal(order) {
    setFedexBoxModal({ order });
  }

  function addFedexBox(order, newBox) {
    setFedexOrderState(order.id, s => ({ ...s, boxes: [...s.boxes, newBox] }));
  }

  function removeFedexBox(orderId, boxId) {
    setFedexOrderState(orderId, s => ({ ...s, boxes: s.boxes.filter(b => b.id !== boxId) }));
  }

  function updateFedexBox(orderId, boxId, updates) {
    setFedexOrderState(orderId, s => ({ ...s, boxes: s.boxes.map(b => b.id === boxId ? { ...b, ...updates } : b) }));
  }

  async function printFedexBoxes(order) {
    const state = getFedexOrderState(order.id);
    if (!state.boxes.length) { alert('Add at least one box first.'); return; }
    const lp = shippingSettings?.zebra_printer_name || 'ZDesigner ZT231';
    setFedexPrinting({ current: 0, total: state.boxes.length, orderRef: order.shopify_order_number || order.order_ref, done: false });
    let printed = 0, failed = 0;
    for (let i = 0; i < state.boxes.length; i++) {
      const box = state.boxes[i];
      setFedexPrinting({ current: i + 1, total: state.boxes.length, orderRef: `${order.shopify_order_number || order.order_ref} box ${i + 1}`, done: false });
      try {
        const { data: result } = await api.post('/api/fedex/create-label', {
          salesOrderId: order.id,
          serviceType: fedexService || 'FEDEX_GROUND',
          // Each box item needs salesOrderItemId for partial Shopify fulfillment tracking
          boxItems: box.items.map(bi => ({
            salesOrderItemId: bi.salesOrderItemId,
            itemId: bi.itemId,
            qty:    bi.qty,
            sku:    bi.sku,
            name:   bi.name,
          })),
          weightLbs:  parseFloat(box.weightLbs) || 1,
          dimensions: box.dimensions || null,
        });
        if (result.labelZpl) {
          try { await printViaQZ(lp, result.labelZpl, true); }
          catch {
            const blob = new Blob([result.labelZpl], { type: 'text/plain' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
            a.download = `fedex_${result.trackingNumber || order.id}_box${i + 1}.zpl`; a.click();
          }
        }
        if (result.packingSlipZpl) {
          try { await printViaQZ(lp, result.packingSlipZpl, true); } catch {}
        }
        printed++;
      } catch (err) {
        failed++;
        const serverMsg = err.response?.data?.error || err.message;
        console.error(`FedEx box ${i + 1} failed for order ${order.id}:`, serverMsg);
        setFedexPrinting({ current: i + 1, total: state.boxes.length, orderRef: `${order.shopify_order_number || order.order_ref} box ${i + 1}`, done: true, printed, failed, errorMsg: serverMsg });
        await new Promise(r => setTimeout(r, 6000));
        setFedexPrinting(null);
        return;
      }
      if (i < state.boxes.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setFedexPrinting({ current: state.boxes.length, total: state.boxes.length, orderRef: '', done: true, printed, failed });
    setTimeout(() => setFedexPrinting(null), 5000);
    // Clear boxes for this order after printing
    setFedexOrderState(order.id, () => ({ boxes: [] }));
    // Force reload shipments so void/reprint buttons appear without a page refresh
    setShipmentMap(m => { const n = { ...m }; delete n[order.id]; return n; });
    refetch();
  }

  // Legacy single-label print (kept for backward compat / select-all flow)
  async function printFedexLabels() {
    const targets = [...fedexSelected].map(id => orders.find(o => o.id === id)).filter(Boolean);
    if (!targets.length) { alert('No orders selected'); return; }
    setFedexPrinting({ current: 0, total: targets.length, orderRef: '', done: false });
    let printed = 0, failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const o = targets[i];
      setFedexPrinting({ current: i + 1, total: targets.length, orderRef: o.shopify_order_number || o.order_ref, done: false });
      try {
        const { data: result } = await api.post('/api/fedex/create-label', { salesOrderId: o.id, serviceType: fedexService || 'FEDEX_GROUND' });
        const lp = shippingSettings?.zebra_printer_name || 'ZDesigner ZT231';
        if (result.labelZpl) {
          try { await printViaQZ(lp, result.labelZpl, true); }
          catch {
            const blob = new Blob([result.labelZpl], { type: 'text/plain' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
            a.download = `fedex_${result.trackingNumber || o.id}.zpl`; a.click();
          }
        }
        if (result.packingSlipZpl) {
          try { await printViaQZ(lp, result.packingSlipZpl, true); } catch {}
        }
        printed++;
      } catch (err) { failed++; console.error(`FedEx label failed for order ${o.id}:`, err.message); }
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setFedexPrinting({ current: targets.length, total: targets.length, orderRef: '', done: true, printed, failed });
    setTimeout(() => setFedexPrinting(null), 5000);
    setFedexSelected(new Set());
  }

  const hasUnsavedFilters = !!(filters.status || filters.openFulfilled || filters.channel || filters.tags);
  const filtersMatchActive = activeViewId !== null && (() => {
    const v = views.find(x => x.id === activeViewId);
    if (!v) return false;
    const vf = { status: '', openFulfilled: '', channel: '', tags: '', ...v.filters };
    return vf.status === filters.status && vf.openFulfilled === (filters.openFulfilled||'') && vf.channel === filters.channel && vf.tags === filters.tags;
  })();

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {showSaveModal && <SaveViewModal filters={filters} onSave={handleSaveView} onClose={() => setShowSaveModal(false)} />}
      {fedexBoxModal && (
        <FedexBoxModal
          order={fedexBoxModal.order}
          boxSizes={boxSizes}
          stagedBoxes={getFedexOrderState(fedexBoxModal.order.id).boxes}
          onAdd={(newBox) => addFedexBox(fedexBoxModal.order, newBox)}
          onClose={() => setFedexBoxModal(null)}
        />
      )}
      {printModalOpen && (
        <PrintOrdersModal
          orders={[...fedexSelected].map(id => orders.find(o => o.id === id)).filter(Boolean)}
          fedexBoxState={fedexBoxState}
          boxSizes={boxSizes}
          onClose={() => setPrintModalOpen(false)}
          onPrint={async (localBoxes) => {
            setPrintModalOpen(false);
            // Print each order's boxes using the modal's local box assignments
            const lp = shippingSettings?.zebra_printer_name || 'ZDesigner ZT231';
            const targets = [...fedexSelected].map(id => orders.find(o => o.id === id)).filter(Boolean);
            setFedexPrinting({ current: 0, total: targets.reduce((s, o) => s + (localBoxes[o.id]?.length || 1), 0), orderRef: '', done: false });
            let printed = 0, failed = 0, globalIdx = 0;
            for (const o of targets) {
              const boxes = localBoxes[o.id] || [];
              for (let i = 0; i < boxes.length; i++) {
                const box = boxes[i];
                globalIdx++;
                setFedexPrinting({ current: globalIdx, total: targets.reduce((s, ord) => s + (localBoxes[ord.id]?.length || 1), 0), orderRef: `${o.shopify_order_number || o.order_ref} box ${i + 1}`, done: false });
                try {
                  const { data: result } = await api.post('/api/fedex/create-label', {
                    salesOrderId: o.id,
                    serviceType: fedexService || 'FEDEX_GROUND',
                    boxItems: box.items.map(bi => ({ salesOrderItemId: bi.salesOrderItemId || bi.itemId, itemId: bi.itemId, qty: bi.qty, sku: bi.sku, name: bi.name })),
                    weightLbs: parseFloat(box.weightLbs) || 1,
                    dimensions: box.dimensions || null,
                    boxSizeId: box.boxSizeId || null,
                  });
                  if (result.labelZpl) {
                    try { await printViaQZ(lp, result.labelZpl, true); }
                    catch {
                      const blob = new Blob([result.labelZpl], { type: 'text/plain' });
                      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                      a.download = `label_${result.trackingNumber || o.id}_box${i + 1}.zpl`; a.click();
                    }
                  }
                  if (result.packingSlipZpl) { try { await printViaQZ(lp, result.packingSlipZpl, true); } catch {} }
                  if (!result.isLQ && result.hazmatFormPdf) {
                    await new Promise(r => setTimeout(r, 2000));
                    const hp = shippingSettings?.hazmat_printer_name || 'HAZMAT Printer';
                    try { await printViaQZ(hp, result.hazmatFormPdf, false); }
                    catch {
                      const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
                      const blob = new Blob([byteArr], { type: 'application/pdf' });
                      window.open(URL.createObjectURL(blob), '_blank');
                    }
                  }
                  printed++;
                } catch (err) {
                  failed++;
                  const serverMsg = err.response?.data?.error || err.message;
                  console.error('Print label failed:', serverMsg);
                  setFedexPrinting({ current: globalIdx, total: globalIdx, orderRef: '', done: true, printed, failed, errorMsg: serverMsg });
                  setTimeout(() => setFedexPrinting(null), 8000);
                  setFedexSelected(new Set());
                  return;
                }
                if (globalIdx < targets.reduce((s, ord) => s + (localBoxes[ord.id]?.length || 1), 0)) await new Promise(r => setTimeout(r, 1500));
              }
            }
            const totalBoxes = targets.reduce((s, o) => s + (localBoxes[o.id]?.length || 1), 0);
            setFedexPrinting({ current: totalBoxes, total: totalBoxes, orderRef: '', done: true, printed, failed });
            setTimeout(() => setFedexPrinting(null), 5000);
            setFedexSelected(new Set());
            refetch();
          }}
        />
      )}
      {hazmatPrintModalOpen && (
        <PrintOrdersModal
          orders={[...hazmatSelected].map(id => orders.find(o => o.id === id)).filter(Boolean)}
          fedexBoxState={{}}
          boxSizes={boxSizes}
          initialSelections={itemSelections}
          onClose={() => setHazmatPrintModalOpen(false)}
          onPrint={async (localBoxes) => {
            setHazmatPrintModalOpen(false);
            const lp = shippingSettings?.zebra_printer_name || 'ZDesigner ZT231';
            const targets = [...hazmatSelected].map(id => orders.find(o => o.id === id)).filter(Boolean);
            const totalBoxes = targets.reduce((s, o) => s + (localBoxes[o.id]?.length || 1), 0);
            setHazmatPrinting({ current: 0, total: totalBoxes, orderRef: '', done: false });
            let printed = 0, failed = 0, globalIdx = 0;
            for (const o of targets) {
              const boxes = localBoxes[o.id] || [];
              for (let i = 0; i < boxes.length; i++) {
                const box = boxes[i];
                globalIdx++;
                setHazmatPrinting({ current: globalIdx, total: totalBoxes, orderRef: `${o.shopify_order_number || o.order_ref} box ${i + 1}`, done: false });
                try {
                  const { data: result } = await api.post('/api/shipping/create-label', {
                    salesOrderId: o.id,
                    boxItems: box.items.filter(bi => bi.qty > 0).map(bi => ({
                      salesOrderItemId: bi.salesOrderItemId || bi.itemId,
                      itemId: bi.itemId,
                      qty: bi.qty,
                      sku: bi.sku,
                      name: bi.name,
                    })),
                    weightLbs: parseFloat(box.weightLbs) || 1,
                    boxSizeId: box.boxSizeId || null,
                  });
                  if (result.labelZpl) {
                    try { await printViaQZ(lp, result.labelZpl, true); }
                    catch {
                      const blob = new Blob([result.labelZpl], { type: 'text/plain' });
                      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                      a.download = `label_${result.trackingNumber || o.id}_box${i + 1}.zpl`; a.click();
                    }
                  }
                  if (result.packingSlipZpl) { try { await printViaQZ(lp, result.packingSlipZpl, true); } catch {} }
                  if (!result.isLQ && result.hazmatFormPdf) {
                    await new Promise(r => setTimeout(r, 2000));
                    const hp = shippingSettings?.hazmat_printer_name || 'HAZMAT Printer';
                    try { await printViaQZ(hp, result.hazmatFormPdf, false); }
                    catch {
                      const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
                      const blob = new Blob([byteArr], { type: 'application/pdf' });
                      window.open(URL.createObjectURL(blob), '_blank');
                    }
                  }
                  printed++;
                } catch (err) {
                  failed++;
                  const serverMsg = err.response?.data?.error || err.message;
                  console.error('Hazmat label failed:', serverMsg);
                  setHazmatPrinting({ current: globalIdx, total: globalIdx, orderRef: '', done: true, printed, failed, errorMsg: serverMsg });
                  setTimeout(() => setHazmatPrinting(null), 8000);
                  setHazmatSelected(new Set());
                  return;
                }
                if (globalIdx < totalBoxes) await new Promise(r => setTimeout(r, 1500));
              }
            }
            setHazmatPrinting({ current: totalBoxes, total: totalBoxes, orderRef: '', done: true, printed, failed });
            setTimeout(() => setHazmatPrinting(null), 5000);
            setShipmentMap(m => { const n = { ...m }; targets.forEach(o => { delete n[o.id]; }); return n; });
            setHazmatSelected(new Set());
            refetch();
          }}
        />
      )}
      {hazmatPrinting && (
        <div style={{ background: hazmatPrinting.done && !hazmatPrinting.failed ? '#dcfce7' : hazmatPrinting.done ? '#fef9c3' : '#eff6ff', border:`1px solid ${hazmatPrinting.done && !hazmatPrinting.failed ? '#86efac' : hazmatPrinting.done ? '#fde047' : '#93c5fd'}`, borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13, display:'flex', alignItems:'center', gap:10, color: hazmatPrinting.done && !hazmatPrinting.failed ? '#166534' : hazmatPrinting.done ? '#713f12' : '#1e40af' }}>
          {!hazmatPrinting.done && <span style={{ width:14, height:14, border:'2px solid rgba(30,64,175,0.3)', borderTopColor:'#1e40af', borderRadius:'50%', animation:'spin 0.7s linear infinite', display:'inline-block', flexShrink:0 }}/>}
          {hazmatPrinting.done
            ? hazmatPrinting.failed === 0
              ? <><i className="ti ti-circle-check"/> Done — {hazmatPrinting.printed} UPS label{hazmatPrinting.printed !== 1 ? 's' : ''} printed</>
              : <><i className="ti ti-alert-triangle"/> {hazmatPrinting.printed} printed, {hazmatPrinting.failed} failed{hazmatPrinting.errorMsg ? `: ${hazmatPrinting.errorMsg}` : ''}</>
            : <>Printing {hazmatPrinting.current} of {hazmatPrinting.total}: <strong>{hazmatPrinting.orderRef}</strong></>}
        </div>
      )}
      {shipModal && (
        <ShipModal
          order={shipModal.order}
          selections={shipModal.selections}
          settings={shippingSettings}
          onSelectionChange={(itemId, sel) => {
            setItemSelections(prev => ({ ...prev, [shipModal.order.id]: { ...(prev[shipModal.order.id] || {}), [itemId]: sel } }));
            setShipModal(m => m ? { ...m, selections: { ...m.selections, [itemId]: sel } } : m);
          }}
          onClose={() => setShipModal(null)}
          onSuccess={(result) => {
            setShipModal(null);
            setPrintResult({ result, order: shipModal.order });
            setItemSelections(prev => { const n = { ...prev }; delete n[shipModal.order.id]; return n; });
          }}
        />
      )}
      {printResult && (
        <PrintResult
          result={printResult.result}
          shipmentId={printResult.result.shipmentId}
          order={printResult.order}
          settings={shippingSettings}
          onClose={() => { setPrintResult(null); refetch(); fetchUnmanifested(); }}
          onMarkPrinted={(type) => { if (type === 'label') { loadShipments(printResult.order.id); fetchUnmanifested(); } }}
        />
      )}

      <div className="page-header">
        <h1>Sales Orders</h1>
        <div className="page-actions">
          {shippingType === 'hazmat' && (
            <button
              className="btn btn-sm"
              style={{ background:'#4f46e5', color:'white', border:'none' }}
              onClick={printManifest}
              disabled={generatingManifest || (unmanifested.count === 0 && checkedManifestIds.size === 0 && hazmatSelected.size === 0 && !(expanded && shipmentMap[expanded]))}
            >
              {generatingManifest
                ? <><span style={{ width:12, height:12, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', borderRadius:'50%', animation:'spin 0.7s linear infinite', display:'inline-block', marginRight:6 }}/> Generating…</>
                : <><i className="ti ti-file-description" /> Print Manifest{checkedManifestIds.size > 0 ? ` (${checkedManifestIds.size} selected)` : unmanifested.count > 0 ? ` (${unmanifested.count})` : expanded && shipmentMap[expanded] ? ' (selected order)' : ''}</>}
            </button>
          )}
          {shippingType === 'hazmat' && hazmatSelected.size > 0 && (
            <button
              className="btn btn-sm"
              style={{ background: '#4f46e5', color: 'white', border: 'none' }}
              onClick={() => {
                // Pre-populate itemSelections with computed defaults for any orders
                // that have no explicit selections yet, so the modal sees the correct state.
                setItemSelections(prev => {
                  const next = { ...prev };
                  [...hazmatSelected].forEach(id => {
                    if (next[id]) return; // already has explicit selections
                    const order = orders.find(o => o.id === id);
                    if (!order) return;
                    const hasFR = (order.items || []).some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
                    const sel = {};
                    (order.items || []).forEach(item => {
                      const wh = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
                      const remaining = item.fulfillableQuantity != null
                        ? item.fulfillableQuantity
                        : Math.max(0, (item.quantity || 0) - (item.fulfilledQuantity || 0));
                      const availableQty = remaining > 0 ? remaining : 0;
                      const checked = availableQty > 0 && (hasFR ? wh > 256 : false);
                      sel[item.id] = { checked, qty: availableQty };
                    });
                    next[id] = sel;
                  });
                  return next;
                });
                setHazmatPrintModalOpen(true);
              }}
            >
              <i className="ti ti-printer" /> Print Orders ({hazmatSelected.size})
            </button>
          )}
          {shippingType === 'fedex' && fedexSelected.size > 0 && (
            <button
              className="btn btn-sm"
              style={{ background: '#4f46e5', color: 'white', border: 'none' }}
              onClick={() => setPrintModalOpen(true)}
            >
              <i className="ti ti-printer" /> Print Orders ({fedexSelected.size})
            </button>
          )}
          {hasUnsavedFilters && !filtersMatchActive && (
            <button className="btn btn-sm btn-secondary" onClick={() => setShowSaveModal(true)}>
              <i className="ti ti-bookmark" /> Save as View
            </button>
          )}
          <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /></button>
        </div>
      </div>

      {/* FedEx print status banner */}
      {fedexPrinting && (
        <div style={{ background: fedexPrinting.done && !fedexPrinting.failed ? '#dcfce7' : fedexPrinting.done ? '#fef9c3' : '#eff6ff', border:`1px solid ${fedexPrinting.done && !fedexPrinting.failed ? '#86efac' : fedexPrinting.done ? '#fde047' : '#93c5fd'}`, borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13, display:'flex', alignItems:'center', gap:10, color: fedexPrinting.done && !fedexPrinting.failed ? '#166534' : fedexPrinting.done ? '#713f12' : '#1e40af' }}>
          {!fedexPrinting.done && <span style={{ width:14, height:14, border:'2px solid rgba(30,64,175,0.3)', borderTopColor:'#1e40af', borderRadius:'50%', animation:'spin 0.7s linear infinite', display:'inline-block', flexShrink:0 }}/>}
          {fedexPrinting.done
            ? fedexPrinting.failed === 0
              ? <><i className="ti ti-circle-check"/> Done — {fedexPrinting.printed} FedEx label{fedexPrinting.printed !== 1 ? 's' : ''} printed</>
              : <><i className="ti ti-alert-triangle"/> {fedexPrinting.printed} printed, {fedexPrinting.failed} failed{fedexPrinting.errorMsg ? `: ${fedexPrinting.errorMsg}` : ''}</>
            : <>Printing {fedexPrinting.current} of {fedexPrinting.total}: <strong>{fedexPrinting.orderRef}</strong></>}
        </div>
      )}

      {/* View Tabs */}
      {!viewsLoading && (
        <div style={{ display:'flex', alignItems:'flex-end', gap:2, marginBottom:-1, overflowX:'auto', paddingBottom:0 }}>
          <ViewTab label="All" count={allCount} active={activeViewId === null} onClick={() => activateView(null)} />
          {views.map(v => (
            <ViewTab key={v.id} label={v.name} count={viewCounts[v.id]} active={activeViewId === v.id}
              onClick={() => activateView(v)} onDelete={() => deleteView(v)}
              shippingType={v.filters?.shippingType} />
          ))}
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ borderTop:'1px solid var(--border)', paddingTop:10 }}>
        <div className="search-input">
          <i className="ti ti-search" />
          <input placeholder="Search order #, customer, tags…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        </div>
        {/* Open / Fulfilled toggle */}
        <div style={{ display:'flex', borderRadius:6, overflow:'hidden', border:'1px solid var(--border)', flexShrink:0 }}>
          {[
            { value: 'open',      label: 'Open' },
            { value: '',          label: 'All' },
            { value: 'fulfilled', label: 'Fulfilled' },
          ].map(opt => (
            <button key={opt.value}
              onClick={() => { setFilters(f => ({ ...f, openFulfilled: opt.value, status: '' })); setPage(0); }}
              style={{
                padding: '4px 13px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: (filters.openFulfilled || '') === opt.value ? 'var(--brand, #2563eb)' : 'var(--surface)',
                color: (filters.openFulfilled || '') === opt.value ? '#fff' : 'var(--text)',
                fontWeight: (filters.openFulfilled || '') === opt.value ? 600 : 400,
                borderRight: opt.value !== 'fulfilled' ? '1px solid var(--border)' : 'none',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        <select value={filters.channel} onChange={e => { setFilters(f => ({ ...f, channel: e.target.value })); setPage(0); }}>
          <option value="">All channels</option>
          {ALL_CHANNELS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <input style={{ width:160 }} className="form-control form-control-sm" placeholder="Tag contains…" value={filters.tags} onChange={e => { setFilters(f => ({ ...f, tags: e.target.value })); setPage(0); }} />
      </div>

      {/* Table */}
      {loading ? <Spinner /> : error ? <ErrorBanner message={error} onRetry={refetch} /> : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {shippingType === 'fedex' && (
                    <th style={{ width:32, textAlign:'center' }}>
                      <input type="checkbox" title="Select All"
                        checked={orders.length > 0 && orders.every(o => fedexSelected.has(o.id))}
                        onChange={e => setFedexSelected(e.target.checked ? new Set(orders.map(o => o.id)) : new Set())}
                        style={{ cursor:'pointer' }} />
                    </th>
                  )}
                  {shippingType === 'hazmat' && (
                    <th style={{ width:32, textAlign:'center' }}>
                      <input type="checkbox" title="Select All"
                        checked={orders.length > 0 && orders.every(o => hazmatSelected.has(o.id))}
                        onChange={e => setHazmatSelected(e.target.checked ? new Set(orders.map(o => o.id)) : new Set())}
                        style={{ cursor:'pointer' }} />
                    </th>
                  )}
                  <th>Order #</th>
                  <th>Channel</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th style={{ textAlign:'right' }}>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const allShipments = Array.isArray(shipmentMap[o.id]) ? shipmentMap[o.id] : (shipmentMap[o.id] ? [shipmentMap[o.id]] : []);
                  const hasUnfulfilled = (o.items || []).some(item => {
                    const isHazmat = item.hazmatEnabled === true || item.hazmatEnabled === 't';
                    const rem = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                    return isHazmat && rem > 0;
                  });
                  const hasAnyShipment = allShipments.some(s => s.status !== 'voided');

                  return (
                    <React.Fragment key={o.id}>
                      <tr style={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                        {shippingType === 'fedex' && (
                          <td onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={fedexSelected.has(o.id)}
                              onChange={() => setFedexSelected(prev => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })} />
                          </td>
                        )}
                        {shippingType === 'hazmat' && (
                          <td onClick={e => e.stopPropagation()} style={{ width:28, textAlign:'center' }}>
                            <input type="checkbox" checked={hazmatSelected.has(o.id)}
                              onChange={() => setHazmatSelected(prev => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })}
                              style={{ cursor:'pointer' }}
                            />
                          </td>
                        )}
                        <td style={{ fontFamily:'monospace', fontSize:12 }}>
                          {o.shopify_order_number && o.shopify_order_number !== o.order_ref
                            ? o.shopify_order_number
                            : o.order_ref?.replace(/^shopify-\d+-/, '#')}
                        </td>
                        <td><Badge status="active" label={o.channel} /></td>
                        <td>{o.customer_name || '—'}</td>
                        <td style={{ fontSize:12 }}>{o.order_date ? new Date(o.order_date).toLocaleDateString() : '—'}</td>
                        <td style={{ fontSize:11, color:'var(--text-tertiary)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.tags || '—'}</td>
                        <td><Badge status={STATUS_MAP[o.status] || 'active'} label={o.status} /></td>
                        <td style={{ textAlign:'right' }}>{o.total_value ? fmt.currency(o.total_value) : '—'}</td>
                        <td onClick={e => e.stopPropagation()} style={{ whiteSpace:'nowrap' }}>
                          {shippingType !== 'hazmat' && (
                            <button className="btn btn-sm" title="Generate Invoice" onClick={() => handlePrintInvoice(o)}>
                              <i className="ti ti-file-invoice" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded === o.id && (
                        <tr>
                          <td colSpan={shippingType === 'fedex' || shippingType === 'hazmat' ? 9 : 8} style={{ background:'var(--surface-2)', padding:'8px 16px' }}>
                            <div style={{ fontSize:12, marginBottom:6, color:'var(--text-secondary)' }}>
                              <strong>Ref:</strong> {o.order_ref}
                              {o.notes && <> &nbsp;|&nbsp; <strong>Notes:</strong> {o.notes}</>}
                              {o.tags && <> &nbsp;|&nbsp; <strong>Tags:</strong> {o.tags}</>}
                            </div>
                            {o.shipping_address1 && (
                              <div style={{ fontSize:12, marginBottom:6, color:'var(--text-secondary)' }}>
                                <strong>Ship to:</strong> {o.shipping_name} — {o.shipping_address1}{o.shipping_address2 && `, ${o.shipping_address2}`}, {o.shipping_city}, {o.shipping_state} {o.shipping_zip}
                              </div>
                            )}
                            {shippingType === 'hazmat' && (o.items || []).length > 0 && (() => {
                              const sel = itemSelections[o.id] || {};
                              const setItemSel = (itemId, field, val) =>
                                setItemSelections(prev => ({ ...prev, [o.id]: { ...prev[o.id], [itemId]: { ...(prev[o.id]?.[itemId] || {}), [field]: val } } }));
                              return (
                                <table style={{ width:'100%', fontSize:12, marginTop:8 }}>
                                  <thead><tr>
                                    <th style={{ padding:'4px 8px', width:28 }}></th>
                                    <th style={{ textAlign:'left', padding:'4px 8px' }}>SKU</th>
                                    <th style={{ textAlign:'left', padding:'4px 8px' }}>Product</th>
                                    <th style={{ textAlign:'right', padding:'4px 8px' }}>Ordered</th>
                                    <th style={{ textAlign:'right', padding:'4px 8px' }}>Fulfilled</th>
                                    <th style={{ textAlign:'right', padding:'4px 8px' }}>Remaining</th>
                                    <th style={{ textAlign:'center', padding:'4px 8px' }}>Ship Qty</th>
                                    <th style={{ textAlign:'left', padding:'4px 8px' }}>Type</th>
                                  </tr></thead>
                                  <tbody>{(o.items || []).map((item, i) => {
                                    const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                                    const availableQty = remaining > 0 ? remaining : (item.quantity || 0);
                                    const canSelect = availableQty > 0;
                                    const itemSel = sel[item.id] || {};
                                    const wh = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
                                    const hasFR = (o.items || []).some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
                                    const checked = canSelect && (itemSel.checked !== undefined ? itemSel.checked : (hasFR ? wh > 256 : false));
                                    const shipQty = itemSel.qty !== undefined ? itemSel.qty : availableQty;
                                    const shipType = wh > 256 ? 'FR' : wh > 0 ? 'LQ' : '—';
                                    return (
                                      <tr key={i} style={{ background: remaining > 0 ? 'var(--amber-bg, #fef3c7)' : 'transparent', opacity: canSelect ? 1 : 0.5 }}>
                                        <td style={{ padding:'4px 8px', textAlign:'center' }}>
                                          {canSelect && <input type="checkbox" checked={checked} onChange={e => setItemSel(item.id, 'checked', e.target.checked)} style={{ cursor:'pointer' }}/>}
                                        </td>
                                        <td style={{ fontFamily:'monospace', padding:'4px 8px' }}>{item.sku}</td>
                                        <td style={{ padding:'4px 8px' }}>{item.productName}</td>
                                        <td style={{ textAlign:'right', padding:'4px 8px' }}>{item.quantity}</td>
                                        <td style={{ textAlign:'right', padding:'4px 8px', color:'var(--text-tertiary)' }}>{item.fulfilledQuantity || 0}</td>
                                        <td style={{ textAlign:'right', padding:'4px 8px', fontWeight: remaining > 0 ? 600 : 'normal', color: remaining > 0 ? '#92400e' : 'var(--text-tertiary)' }}>{remaining}</td>
                                        <td style={{ textAlign:'center', padding:'2px 8px' }}>
                                          {canSelect && checked && (
                                            <input type="number" min={1} max={Math.max(1, availableQty)} value={shipQty || 1}
                                              onChange={e => setItemSel(item.id, 'qty', Math.min(remaining, Math.max(1, parseInt(e.target.value)||1)))}
                                              style={{ width:50, textAlign:'center', padding:'2px 4px', border:'1px solid var(--border)', borderRadius:3, fontSize:11 }}/>
                                          )}
                                        </td>
                                        <td style={{ padding:'4px 8px' }}>
                                          {shipType !== '—' && (
                                            <span style={{ fontSize:10, padding:'1px 5px', borderRadius:3, background: shipType==='LQ' ? '#dcfce7' : '#fef3c7', color: shipType==='LQ' ? '#166534' : '#92400e', fontWeight:600 }}>{shipType}</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}</tbody>
                                </table>
                              );
                            })()}
                            {!shippingType && (o.items || []).length > 0 && (
                              <table style={{ width:'100%', fontSize:12, marginTop:8 }}>
                                <thead><tr style={{ borderBottom:'1px solid var(--border)' }}>
                                  <th style={{ textAlign:'left', padding:'4px 8px' }}>SKU</th>
                                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Product</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Qty</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Fulfilled</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Unit Price</th>
                                </tr></thead>
                                <tbody>{o.items.map((item, i) => (
                                  <tr key={i}>
                                    <td style={{ fontFamily:'monospace', padding:'4px 8px' }}>{item.sku}</td>
                                    <td style={{ padding:'4px 8px' }}>{item.productName}</td>
                                    <td style={{ textAlign:'right', padding:'4px 8px' }}>{item.quantity}</td>
                                    <td style={{ textAlign:'right', padding:'4px 8px', color:'var(--text-tertiary)' }}>{item.fulfilledQuantity || 0}</td>
                                    <td style={{ textAlign:'right', padding:'4px 8px' }}>{fmt.currency(item.unitPrice)}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            )}
                            {shippingType === 'fedex' && (o.items || []).length > 0 && (() => {
                              const fState = getFedexOrderState(o.id);
                              return (
                                <div style={{ marginTop:8 }}>
                                  {/* Line items summary */}
                                  <table style={{ width:'100%', fontSize:12, marginBottom:12 }}>
                                    <thead><tr style={{ borderBottom:'1px solid var(--border)' }}>
                                      <th style={{ textAlign:'left', padding:'4px 8px' }}>SKU</th>
                                      <th style={{ textAlign:'left', padding:'4px 8px' }}>Product</th>
                                      <th style={{ textAlign:'right', padding:'4px 8px' }}>Ordered</th>
                                      <th style={{ textAlign:'right', padding:'4px 8px' }}>Fulfilled</th>
                                      <th style={{ textAlign:'right', padding:'4px 8px' }}>Remaining</th>
                                      <th style={{ textAlign:'right', padding:'4px 8px' }}>Unit Price</th>
                                    </tr></thead>
                                    <tbody>{(o.items || []).map((item, i) => {
                                      const remaining = item.fulfillableQuantity != null
                                        ? item.fulfillableQuantity
                                        : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                                      return (
                                        <tr key={i} style={{ borderBottom:'1px solid var(--border-subtle, #f5f5f5)', background: remaining > 0 ? 'transparent' : 'var(--surface-2)' }}>
                                          <td style={{ fontFamily:'monospace', padding:'4px 8px', fontSize:11 }}>{item.sku}</td>
                                          <td style={{ padding:'4px 8px' }}>{item.productName}</td>
                                          <td style={{ textAlign:'right', padding:'4px 8px' }}>{item.quantity}</td>
                                          <td style={{ textAlign:'right', padding:'4px 8px', color:'var(--text-tertiary)' }}>{item.fulfilledQuantity || 0}</td>
                                          <td style={{ textAlign:'right', padding:'4px 8px', fontWeight: remaining > 0 ? 600 : 'normal', color: remaining > 0 ? 'inherit' : 'var(--text-tertiary)' }}>{remaining}</td>
                                          <td style={{ textAlign:'right', padding:'4px 8px', color:'var(--text-secondary)' }}>{fmt.currency(item.unitPrice)}</td>
                                        </tr>
                                      );
                                    })}</tbody>
                                  </table>
                                  <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
                                    <select value={fedexService} onChange={e => setFedexService(e.target.value)}
                                      style={{ fontSize:12, padding:'4px 6px', borderRadius:4, border:'1px solid var(--border)', height:28 }}>
                                      <option value="FEDEX_GROUND">Ground</option>
                                      <option value="FEDEX_EXPRESS_SAVER">Express Saver</option>
                                      <option value="FEDEX_GROUND_ECONOMY">Ground Economy</option>
                                      <option value="GROUND_HOME_DELIVERY">Home Delivery</option>
                                      <option value="PRIORITY_OVERNIGHT">Priority Overnight</option>
                                      <option value="STANDARD_OVERNIGHT">Standard Overnight</option>
                                      <option value="FEDEX_2_DAY">2 Day</option>
                                    </select>
                                    <button className="btn btn-sm btn-primary" onClick={() => openFedexBoxModal(o)}>
                                      <i className="ti ti-plus" /> Add Box
                                    </button>
                                  </div>

                                  {/* Staged boxes */}
                                  {fState.boxes.length > 0 && (
                                    <div style={{ border:'1px solid var(--border)', borderRadius:6, overflow:'hidden', marginBottom:8 }}>
                                      <div style={{ background:'var(--surface-3)', padding:'6px 12px', fontSize:11, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                                        Boxes to Ship ({fState.boxes.length})
                                      </div>
                                      {fState.boxes.map((box, bi) => {
                                        const selectedBoxSize = boxSizes.find(b => b.id === box.boxSizeId);
                                        const dims = box.dimensions;
                                        return (
                                          <div key={box.id} style={{ padding:'10px 12px', borderTop: bi > 0 ? '1px solid var(--border)' : 'none', display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap' }}>
                                            <div style={{ fontWeight:600, fontSize:12, minWidth:40 }}>Box {bi + 1}</div>
                                            <div style={{ flex:1, fontSize:12 }}>
                                              {box.items.map(bi2 => (
                                                <span key={bi2.salesOrderItemId || bi2.itemId} style={{ display:'block', color:'var(--text-secondary)', marginBottom:2 }}>
                                                  <span style={{ fontFamily:'monospace', fontSize:11 }}>{bi2.sku}</span>
                                                  {bi2.name ? <span style={{ color:'var(--text-tertiary)' }}> – {bi2.name}</span> : null}
                                                  {' '}×{bi2.qty}
                                                </span>
                                              ))}
                                            </div>
                                            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', fontSize:11 }}>
                                              {selectedBoxSize && (
                                                <span style={{ color:'var(--text-secondary)', whiteSpace:'nowrap' }}>
                                                  {selectedBoxSize.name} ({selectedBoxSize.length_in}×{selectedBoxSize.width_in}×{selectedBoxSize.height_in}")
                                                </span>
                                              )}
                                              {!selectedBoxSize && dims && (
                                                <span style={{ color:'var(--text-secondary)', whiteSpace:'nowrap' }}>
                                                  {dims.length}×{dims.width}×{dims.height}"
                                                </span>
                                              )}
                                              <span style={{ color:'var(--text-secondary)', whiteSpace:'nowrap' }}>
                                                {parseFloat(box.weightLbs).toFixed(2)} lbs
                                              </span>
                                              <button className="btn btn-sm" style={{ color:'var(--red)', padding:'2px 6px' }} onClick={() => removeFedexBox(o.id, box.id)} title="Remove box">
                                                <i className="ti ti-trash" />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      <div style={{ padding:'10px 12px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end' }}>
                                        <button className="btn btn-sm" style={{ background:'#4f46e5', color:'white', border:'none' }}
                                          onClick={() => printFedexBoxes(o)}
                                          disabled={!!fedexPrinting && !fedexPrinting.done}>
                                          <i className="ti ti-printer" /> Print {fState.boxes.length} Label{fState.boxes.length !== 1 ? 's' : ''}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {shippingType === 'hazmat' && allShipments.length > 0 && (
                              <div style={{ marginTop:8 }}>
                                {allShipments.filter(s => s.status !== 'voided').map(s => (
                                  <div key={s.id} style={{ fontSize:12, display:'flex', alignItems:'center', gap:12, padding:'6px 0', borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
                                    <span>
                                      <strong>{s.tracking_number}</strong>
                                      {s.ship_type && <>&nbsp;|&nbsp;<strong>Mode:</strong> {s.ship_type}</>}
                                      {s.weight_lbs && <>&nbsp;|&nbsp;<strong>Weight:</strong> {s.weight_lbs} lbs</>}
                                      &nbsp;|&nbsp;<strong>Label:</strong> {s.label_printed_at ? new Date(s.label_printed_at).toLocaleString() : 'Not printed'}
                                      {s.ship_type === 'hazmat' && <>&nbsp;|&nbsp;<strong>Form:</strong> {s.form_printed_at ? new Date(s.form_printed_at).toLocaleString() : 'Not printed'}</>}
                                      {s.manifested_at && <span style={{ color:'var(--green)', marginLeft:8 }}>✓ Manifested</span>}
                                    </span>
                                    <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                                      <button className="btn btn-sm" onClick={() => reprintLabel(s)}>
                                        <i className="ti ti-printer"/> Label
                                      </button>
                                      {s.ship_type === 'hazmat' && (
                                        <button className="btn btn-sm" onClick={() => reprintForm(s)}>
                                          <i className="ti ti-file-text"/> Form
                                        </button>
                                      )}
                                      <button className="btn btn-sm" style={{ color:'var(--red)' }}
                                        onClick={async () => {
                                          if (!window.confirm(`Void shipment ${s.tracking_number}?`)) return;
                                          try {
                                            await api.post(`/api/shipping/${s.id}/void`);
                                            loadShipments(o.id);
                                          } catch (e) { alert('Void failed: ' + (e.response?.data?.error || e.message)); }
                                        }}>
                                        <i className="ti ti-ban"/> Void
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {shippingType === 'fedex' && allShipments.length > 0 && (() => {
                              const active = allShipments.filter(s => s.status !== 'voided');
                              const voided = allShipments.filter(s => s.status === 'voided');
                              if (!active.length && !voided.length) return null;
                              return (
                                <div style={{ marginTop:12 }}>
                                  <div style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>
                                    Printed Labels ({active.length} active{voided.length > 0 ? `, ${voided.length} voided` : ''})
                                  </div>
                                  <div style={{ border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
                                    {active.map((s, bi) => (
                                      <div key={s.id} style={{ borderTop: bi > 0 ? '1px solid var(--border)' : 'none', padding:'10px 12px' }}>
                                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                                          <div style={{ fontSize:12 }}>
                                            <span style={{ fontWeight:700, fontFamily:'monospace' }}>{s.tracking_number}</span>
                                            {s.weight_lbs && <span style={{ color:'var(--text-secondary)', marginLeft:10 }}>{s.weight_lbs} lbs</span>}
                                            {s.dimensions && <span style={{ color:'var(--text-tertiary)', marginLeft:10, fontSize:11 }}>{s.dimensions}</span>}
                                            {s.manifested_at && <span style={{ color:'var(--green)', marginLeft:10, fontSize:11 }}>✓ Manifested</span>}
                                            <span style={{ color:'var(--text-tertiary)', marginLeft:10, fontSize:11 }}>
                                              {s.label_printed_at ? new Date(s.label_printed_at).toLocaleString() : 'Not printed'}
                                            </span>
                                          </div>
                                          <div style={{ display:'flex', gap:4 }}>
                                            <button className="btn btn-sm" onClick={() => reprintLabel(s)}>
                                              <i className="ti ti-printer"/> Label
                                            </button>
                                            <button className="btn btn-sm" style={{ color:'var(--red)' }}
                                              onClick={async () => {
                                                if (!window.confirm(`Void box ${bi + 1} (${s.tracking_number})?`)) return;
                                                try {
                                                  await api.post(`/api/shipping/${s.id}/void`);
                                                  loadShipments(o.id);
                                                } catch (e) { alert('Void failed: ' + (e.response?.data?.error || e.message)); }
                                              }}>
                                              <i className="ti ti-ban"/> Void
                                            </button>
                                          </div>
                                        </div>
                                        {(s.items || []).length > 0 && (
                                          <div style={{ marginTop:6, paddingLeft:4 }}>
                                            {s.items.map((it, ii) => (
                                              <div key={ii} style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:'1.6' }}>
                                                <span style={{ fontFamily:'monospace', color:'var(--text-primary)' }}>{it.sku}</span>
                                                {' — '}{it.productName} <span style={{ color:'var(--text-tertiary)' }}>×{it.quantity}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {voided.length > 0 && (
                                      <div style={{ borderTop:'1px solid var(--border)', padding:'6px 12px', background:'var(--surface-2)' }}>
                                        {voided.map((s, vi) => (
                                          <div key={s.id} style={{ fontSize:11, color:'var(--text-tertiary)', textDecoration:'line-through', lineHeight:'1.8' }}>
                                            {s.tracking_number} (voided)
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {!orders.length && <EmptyState icon="ti-shopping-cart" message="No orders found" />}
          </div>
          {totalPages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:12, fontSize:13 }}>
              <span style={{ color:'var(--text-tertiary)' }}>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()} orders</span>
              <div style={{ display:'flex', gap:4 }}>
                <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
                <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                <span style={{ padding:'4px 10px', fontSize:12 }}>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                <button className="btn btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// build: 1791838590
